import React, { useState } from "react";
import { api, apiError } from "../../lib/api";
import { Sparkles } from "lucide-react";
import { QuizOptions } from "../QuizOptions";

const scoreOf = (qs, answers) => qs.reduce((n, q, i) => n + (answers[i] === q.answer ? 1 : 0), 0);

const saveWrongAnswers = (qs, answers, sourceId) =>
  qs.forEach((q, i) => {
    if (answers[i] === undefined || answers[i] === q.answer) return;
    api.post("/me/mistakes", { source: "auto-quiz", source_id: sourceId, q: q.q, options: q.options, correct_index: q.answer, chosen_index: answers[i], explanation: q.explanation || "" })
      .catch(() => { /* anonymous users have no mistake log */ });
  });

function QuizFooter({ state, allAnswered, onGrade, score, total }) {
  if (state === "ready") {
    return <button onClick={onGrade} disabled={!allAnswered} data-testid="autoquiz-grade" className="rounded-md bg-lime-500 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-400 disabled:opacity-50">Grade</button>;
  }
  return (
    <>
      <div className="rounded-md border border-lime-400/40 bg-lime-500/10 px-3 py-1 text-sm text-lime-200" data-testid="autoquiz-score">Score <b className="font-mono-num">{score}</b> / {total}</div>
      {score >= 2 && <span className="text-xs text-lime-300" data-testid="autoquiz-passed">✓ Auto-marked as read</span>}
    </>
  );
}

function useAutoQuiz(year, order, onPass) {
  const [state, setState] = useState("idle"); // idle | loading | ready | graded | error
  const [qs, setQs] = useState([]);
  const [answers, setAnswers] = useState({});
  const [err, setErr] = useState(null);

  const load = async () => {
    setState("loading");
    setErr(null);
    try {
      const { data } = await api.post(`/curriculum/${year}/${order}/quiz`);
      setQs(data.questions);
      setAnswers({});
      setState("ready");
    } catch (e) {
      setErr(apiError(e, "Could not generate quiz"));
      setState("error");
    }
  };
  const grade = () => {
    setState("graded");
    saveWrongAnswers(qs, answers, `${year}-${order}`);
    const s = scoreOf(qs, answers);
    if (s >= 2 && onPass) onPass(s, qs.length);
  };
  const choose = (qi, oi) => setAnswers({ ...answers, [qi]: oi });
  return { state, qs, answers, err, load, grade, choose };
}

function AutoQuizQuestion({ q, qi, chosen, graded, onChoose }) {
  return (
    <div data-testid={`autoquiz-q-${qi}`}>
      <div className="font-display text-sm font-semibold text-white">Q{qi + 1}. {q.q}</div>
      <QuizOptions options={q.options} chosen={chosen} correctIndex={q.answer} graded={graded} compact onChoose={(oi) => onChoose(qi, oi)} testId={(oi) => `autoquiz-q-${qi}-opt-${oi}`} />
      {graded && q.explanation && <div className="mt-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300">{q.explanation}</div>}
    </div>
  );
}

export function AutoQuiz({ year, order, onPass }) {
  const { state, qs, answers, err, load, grade, choose } = useAutoQuiz(year, order, onPass);
  const allAnswered = qs.length > 0 && qs.every((_, i) => i in answers);
  const showQs = state === "ready" || state === "graded";

  return (
    <section className="rounded-md border border-white/10 border-t-2 border-t-lime-400 bg-black/40 p-6" data-testid="auto-quiz">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-lime-300">Lock it in</div>
          <h3 className="font-display mt-1 text-xl font-semibold text-white">3-question mini-quiz</h3>
        </div>
        {state === "idle" && <button onClick={load} data-testid="autoquiz-start" className="rounded-md bg-lime-500 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-400">Start</button>}
        {(showQs || state === "error") && <button onClick={load} data-testid="autoquiz-new" className="rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs hover:bg-white/10">New questions</button>}
      </div>

      {state === "loading" && <div className="mt-4 text-sm text-slate-400"><Sparkles size={14} className="mr-1 inline" /> Generating…</div>}
      {state === "error" && <div className="mt-4 rounded-md border border-red-400/40 bg-red-500/10 p-3 text-xs text-red-200">{err}</div>}
      {showQs && (
        <div className="mt-4 space-y-4">
          {qs.map((q, qi) => <AutoQuizQuestion key={`${qi}-${q.q}`} q={q} qi={qi} chosen={answers[qi]} graded={state === "graded"} onChoose={choose} />)}
          <div className="flex items-center gap-3 pt-2">
            <QuizFooter state={state} allAnswered={allAnswered} onGrade={grade} score={scoreOf(qs, answers)} total={qs.length} />
          </div>
        </div>
      )}
    </section>
  );
}
